import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
  getToken, clearToken,
  departments, designations, projects, account,
} from './api/client';
import Login from './screens/Login';
import Employees from './screens/Employees';
import LookupAdmin from './screens/LookupAdmin';
import GitMetrics from './screens/GitMetrics';
import MyTime from './screens/MyTime';
import MyLeave from './screens/MyLeave';
import LeaveAdmin from './screens/LeaveAdmin';
import Approvals from './screens/Approvals';
import TeamAttendance from './screens/TeamAttendance';
import ChangePassword from './screens/ChangePassword';
import Costing from './screens/Costing';
import ForcedPasswordChange from './screens/ForcedPasswordChange';
import MyPay from './screens/MyPay';
import ProfileDialog from './components/ProfileDialog';
import AppHeader from './components/AppHeader';
import AuthLayout from './components/AuthLayout';
import { notify } from './notify';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import './App.css';

// The boards pull in drag-and-drop; load them only when Work is opened.
const Work = lazy(() => import('./screens/Work'));
// The public client portal: no login, and staff never download it.
const PortalPage = lazy(() => import('./screens/PortalPage'));
// The client workspace: invited client people with their own email-link sign-in.
const ClientApp = lazy(() => import('./client/ClientApp'));
// Payroll is for HR and the MD only; nobody else downloads it.
const Payroll = lazy(() => import('./screens/Payroll'));
// Meetings carries the video player and transcript; load it only when opened.
const Meetings = lazy(() => import('./screens/Meetings'));

/** "#/portal/<token>" -> token, for the public client portal. */
function portalTokenFromHash(hash) {
  const m = /^#\/portal\/([A-Za-z0-9_-]{20,128})/.exec(hash || '');
  return m ? m[1] : null;
}

/** "#/client..." is the client workspace, which never uses the staff login. */
const isClientHash = (hash) => /^#\/client(\/|$)/.test(hash || '');

/** "#/work/WEB-12" -> "WEB-12": the ticket a shared link or notification points at. */
function workKeyFromHash(hash) {
  const m = /^#\/work\/([A-Za-z][A-Za-z0-9]{1,9}-\d{1,7})$/.exec(hash || '');
  return m ? m[1].toUpperCase() : null;
}

/** "#/meetings/12" -> 12: the recorded meeting a shared link points at. */
function meetingIdFromHash(hash) {
  const m = /^#\/meetings\/(\d{1,12})$/.exec(hash || '');
  return m ? Number(m[1]) : null;
}

/** "#/pay" opens Pay & documents - where a payslip notification or push lands. */
const isPayHash = (hash) => /^#\/pay\b/.test(hash || '');

export default function App() {
  const [authed, setAuthed] = useState(!!getToken());
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(false);
  const [tab, setTab] = useState('work');
  const [loadError, setLoadError] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // Last sub-tab opened in each top-bar section.
  const [lastSub, setLastSub] = useState({});
  // Sign-in password, kept in memory only until a forced change consumes it.
  const [signInPassword, setSignInPassword] = useState(null);
  // A ticket to open in Work - from the address bar (#/work/KEY) or the notification bell.
  const [deepLinkKey, setDeepLinkKey] = useState(() => workKeyFromHash(window.location.hash));
  // A meeting to open in Meetings - from the address bar (#/meetings/<id>).
  const [deepLinkMeetingId, setDeepLinkMeetingId] = useState(() => meetingIdFromHash(window.location.hash));
  const [portalToken, setPortalToken] = useState(() => portalTokenFromHash(window.location.hash));
  const [clientView, setClientView] = useState(() => isClientHash(window.location.hash));

  const loadMe = useCallback(async () => {
    setLoadingMe(true);
    setLoadError(null);
    try {
      const m = await account.me();
      setMe(m);
      // Everyone lands on Work - it's what people use all day. A login with
      // no employee record has no Work of its own, so HR lands on the roster.
      // A link to a ticket, a meeting or the pay page wins over the usual landing tab.
      const h = window.location.hash;
      if (meetingIdFromHash(h) && (m.linked || m.isAdmin)) setTab('meetings');
      else setTab(isPayHash(h) && m.linked ? 'pay' : (workKeyFromHash(h) || m.linked ? 'work' : 'employees'));
    } catch (e) {
      // A stale token surfaces here as a 401 rather than as a blank screen.
      if (e.status === 401) {
        clearToken();
        setAuthed(false);
      } else {
        setLoadError(e.message);
      }
    } finally {
      setLoadingMe(false);
    }
  }, []);

  useEffect(() => {
    // A signed-in browser opening a client portal link shouldn't load the staff app behind it.
    if (authed && !portalToken && !clientView) loadMe();
  }, [authed, loadMe, portalToken, clientView]);

  // Back/forward or a pasted link while the app is open.
  useEffect(() => {
    const onHash = () => {
      setPortalToken(portalTokenFromHash(window.location.hash));
      setClientView(isClientHash(window.location.hash));
      const key = workKeyFromHash(window.location.hash);
      if (key) { setDeepLinkKey(key); setTab('work'); }
      const meetingId = meetingIdFromHash(window.location.hash);
      if (meetingId) { setDeepLinkMeetingId(meetingId); setTab('meetings'); }
      if (isPayHash(window.location.hash)) setTab('pay');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const openTicket = useCallback((key) => {
    if (!key) return;
    setDeepLinkKey(key);
    setTab('work');
  }, []);
  const deepLinkHandled = useCallback(() => setDeepLinkKey(null), []);
  const meetingLinkHandled = useCallback(() => setDeepLinkMeetingId(null), []);

  function signOut() {
    clearToken();
    setMe(null);
    setSignInPassword(null);
    setAuthed(false);
  }

  // If HR resets the password while someone is signed in, their next request
  // is refused with PASSWORD_CHANGE_REQUIRED - reload the profile so the
  // forced-change screen takes over instead of showing a raw error.
  useEffect(() => {
    const onRequired = () => { if (authed) loadMe(); };
    window.addEventListener('erm:password-change-required', onRequired);
    return () => window.removeEventListener('erm:password-change-required', onRequired);
  }, [authed, loadMe]);

  if (clientView) {
    return (
      <Suspense fallback={<Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>}>
        <ClientApp />
      </Suspense>
    );
  }

  if (portalToken) {
    return (
      <Suspense fallback={<Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>}>
        <PortalPage token={portalToken} />
      </Suspense>
    );
  }

  if (!authed) {
    return <Login onSuccess={(user, password) => { setSignInPassword(password); setAuthed(true); }} />;
  }
  // No profile and no failure yet means the first fetch is still on its way -
  // without this, the "can't load" screen flashes for one frame after sign-in.
  if (!me && (loadingMe || !loadError)) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Loading your account" />
      </Box>
    );
  }

  // Signed in but the profile call failed (e.g. the server is an older build
  // without ERM, or unreachable). Say so instead of spinning on "Loading…".
  if (!me) {
    return (
      <AuthLayout title="Can't load your account">
        <Stack spacing={2}>
          <Alert severity="error">{loadError || 'The server did not respond.'}</Alert>
          <Box sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center' }}>
            If this persists, the ERM service may not be deployed yet. Contact HR or IT.
          </Box>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
            <Button variant="contained" onClick={loadMe}>Try again</Button>
            <Button color="inherit" onClick={signOut}>Sign out</Button>
          </Stack>
        </Stack>
      </AuthLayout>
    );
  }

  if (me.mustChangePassword) {
    return (
      <ForcedPasswordChange
        me={me}
        currentPassword={signInPassword}
        onSignOut={signOut}
        onDone={async () => {
          setSignInPassword(null);
          notify.success('Password set. Welcome to Cloudmyle ERM.');
          await loadMe();
        }}
      />
    );
  }

  const isAdmin = !!me.isAdmin;
  const isManager = !!me.isManager;
  const linked = !!me.linked;

  const canPayroll = !!me.canPayroll;

  // Top bar: what people use every day. Each section has its own sub-tabs;
  // "pay" has no top-bar tab and opens from the account menu.
  const sections = [];
  if (linked || isAdmin) sections.push({ key: 'work', label: 'Work', subs: [{ key: 'work', label: 'Work' }] });
  if (linked || isAdmin) {
    sections.push({ key: 'meetings', label: 'Meetings', subs: [
      { key: 'meetings', label: 'Meetings' },
      { key: 'meetings-upcoming', label: 'Upcoming' },
      { key: 'meetings-settings', label: 'Settings' },
    ] });
  }
  if (linked) {
    sections.push({ key: 'time', label: 'My time', subs: [
      { key: 'my-time', label: 'Timesheet & attendance' },
      { key: 'my-leave', label: 'Leave' },
    ] });
  }
  if (isManager || isAdmin) {
    const subs = [{ key: 'approvals', label: 'Approvals' }, { key: 'attendance', label: 'Attendance' }];
    if (!isAdmin) subs.push({ key: 'costing', label: 'Project hours' });
    sections.push({ key: 'team', label: 'Team', subs });
  }
  if (isAdmin || canPayroll) {
    const subs = [];
    if (isAdmin) {
      subs.push({ key: 'employees', label: 'Employees' });
      subs.push({ key: 'leave-admin', label: 'Leave balances' });
      subs.push({ key: 'projects', label: 'Projects' });
      subs.push({ key: 'departments', label: 'Departments' });
      subs.push({ key: 'designations', label: 'Designations' });
    }
    if (canPayroll) subs.push({ key: 'payroll', label: 'Payroll' });
    if (isAdmin) {
      subs.push({ key: 'costing', label: 'Costing' });
      subs.push({ key: 'git', label: 'Git metrics' });
    }
    sections.push({ key: 'admin', label: 'Admin', subs });
  }
  const hidden = linked ? [{ key: 'pay', label: 'Pay & documents' }] : [];
  const allSubs = sections.flatMap((sec) => sec.subs.map((x) => ({ ...x, section: sec.key }))).concat(hidden);
  const firstSub = allSubs[0] ? allSubs[0].key : false;
  const current = allSubs.some((x) => x.key === tab) ? tab : firstSub;
  const currentSub = allSubs.find((x) => x.key === current);
  const section = sections.find((sec) => currentSub && sec.key === currentSub.section) || null;

  // Clicking a section returns to the sub-tab last open in it.
  function openSection(key) {
    const sec = sections.find((x) => x.key === key);
    if (!sec) return;
    const last = lastSub[key];
    setTab(sec.subs.some((x) => x.key === last) ? last : sec.subs[0].key);
  }
  function openSub(key) {
    if (section) setLastSub((cur) => ({ ...cur, [section.key]: key }));
    setTab(key);
  }

  // notify.* are stable module functions, so screens can keep them in hook deps.
  const pass = { me, onError: notify.error, onNotice: notify.success };

  return (
    <div className="app">
      <AppHeader me={me} tabs={sections} current={section ? section.key : false} onTab={openSection}
                 onOpenTicket={openTicket} onOpenPay={linked ? () => setTab('pay') : null}
                 onOpenProfile={linked ? () => setProfileOpen(true) : null}
                 onChangePassword={() => setPwOpen(true)} onSignOut={signOut} />

      {profileOpen && <ProfileDialog me={me} onClose={() => setProfileOpen(false)} />}

      <ChangePassword
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        onDone={(msg) => { setPwOpen(false); notify.success(msg); }}
      />

      {/* Boards need the width; everything else keeps the readable column. */}
      <main className={`main${current === 'work' || current === 'payroll' || current === 'meetings' ? ' main-wide' : ''}`}>
        {!linked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Your login is not linked to an employee record</AlertTitle>
            Ask HR to link it from the Employees screen. Until then, timesheets and leave are unavailable.
          </Alert>
        )}

        {section && section.subs.length > 1 && (
          <nav className="subtabs section-nav" aria-label={`${section.label} sections`}>
            {section.subs.map((x) => (
              <button key={x.key} className={`tab ${current === x.key ? 'tab-on' : ''}`}
                      aria-current={current === x.key ? 'page' : undefined} onClick={() => openSub(x.key)}>
                {x.label}
              </button>
            ))}
          </nav>
        )}

        {current === 'my-time' && linked && <MyTime {...pass} />}
        {current === 'my-leave' && linked && <MyLeave {...pass} />}
        {current === 'work' && (
          <Suspense fallback={<Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>}>
            <Work {...pass} deepLinkKey={deepLinkKey} onDeepLinkHandled={deepLinkHandled} />
          </Suspense>
        )}
        {(current === 'meetings' || current === 'meetings-upcoming' || current === 'meetings-settings') && (
          <Suspense fallback={<Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>}>
            <Meetings {...pass} view={current} deepLinkId={deepLinkMeetingId} onDeepLinkHandled={meetingLinkHandled}
                      onOpenSettings={() => openSub('meetings-settings')} />
          </Suspense>
        )}
        {current === 'approvals' && <Approvals {...pass} />}
        {current === 'attendance' && <TeamAttendance {...pass} />}
        {current === 'costing' && <Costing {...pass} />}
        {current === 'employees' && <Employees />}
        {current === 'leave-admin' && <LeaveAdmin {...pass} />}
        {current === 'git' && <GitMetrics />}
        {current === 'pay' && linked && <MyPay {...pass} />}
        {current === 'payroll' && canPayroll && (
          <Suspense fallback={<Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>}>
            <Payroll {...pass} />
          </Suspense>
        )}

        {current === 'projects' && (
          <LookupAdmin
            title="Projects"
            subtitle="Codes employees book time against. Closing keeps history intact."
            resource={{ ...projects, deactivate: projects.close }}
            prefill={async () => ({ code: (await projects.nextCode()).code })}
            fields={[
              { key: 'code', label: 'Code', placeholder: 'auto',
                hint: 'Auto-generated. Type INT- for the next internal code.' },
              { key: 'name', label: 'Name', required: true },
              { key: 'clientName', label: 'Client' },
              { key: 'description', label: 'Description', type: 'textarea', wide: true },
            ]}
          />
        )}

        {current === 'departments' && (
          <LookupAdmin
            title="Departments"
            subtitle="Deleting is blocked while employees are assigned — deactivate instead."
            resource={departments}
            canDelete
            fields={[
              { key: 'name', label: 'Name', required: true },
              { key: 'code', label: 'Code', placeholder: 'ENG' },
              { key: 'headEmployeeId', label: 'Head employee ID', type: 'number' },
              { key: 'description', label: 'Description', type: 'textarea', wide: true },
            ]}
          />
        )}

        {current === 'designations' && (
          <LookupAdmin
            title="Designations"
            subtitle="Level is a seniority band (1 = junior) used later by payroll grades."
            resource={designations}
            fields={[
              { key: 'title', label: 'Title', required: true },
              { key: 'level', label: 'Level', type: 'number', placeholder: '2' },
              { key: 'description', label: 'Description', type: 'textarea', wide: true },
            ]}
          />
        )}
      </main>
    </div>
  );
}
